import { useTranslation } from 'react-i18next';
import { storeConsentPreferences, respectDNT } from '@/utils/cookie-consent';
import { useEffect, useState } from 'react';

interface CookieConsentBannerProps {
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onCustomize: () => void;
}

export const CookieConsentBanner = ({
  onAcceptAll,
  onRejectAll,
  onCustomize,
}: CookieConsentBannerProps) => {
  const { t } = useTranslation('cookie-consent');
  const [dntEnabled, setDntEnabled] = useState(false);

  useEffect(() => {
    // Check DNT on mount
    const isDnt = respectDNT();
    setDntEnabled(isDnt);

    // Auto-reject if DNT is enabled
    if (isDnt) {
      storeConsentPreferences({
        essential: true,
        analytics: false,
        marketing: false,
      });
      onRejectAll();
    }
  }, [onRejectAll]);

  // Don't show banner if DNT auto-rejected
  if (dntEnabled) {
    return null;
  }

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-base-100 border-t border-base-300 shadow-lg animate-slideUpBanner"
      role="region"
      aria-label="Cookie consent"
    >
      <div className="container mx-auto px-4 py-4 md:py-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Message */}
          <div className="flex-1">
            <p className="text-sm md:text-base text-base-content">
              {t('message')}
              {' '}
              <a
                href="/privacy"
                className="link link-primary underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('learnMore')}
              </a>
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 min-w-fit">
            <button
              onClick={onAcceptAll}
              className="btn btn-primary btn-sm md:btn-md"
              aria-label="Accept all cookies"
            >
              {t('acceptAll')}
            </button>
            <button
              onClick={onRejectAll}
              className="btn btn-ghost btn-sm md:btn-md"
              aria-label="Reject non-essential cookies"
            >
              {t('rejectAll')}
            </button>
            <button
              onClick={onCustomize}
              className="btn btn-outline btn-sm md:btn-md"
              aria-label="Customize cookie preferences"
            >
              {t('customize')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
